/** Add a note ("Chrissy phoned about …"): write it, Save (⌘Enter). Like a logged call, it is on the timeline at once and written after six seconds, so Undo (in the toast, or ⌘Z) leaves nothing in the record and puts the words back. Notes stay in Keystone: never in ActiveCampaign, never in CE21. */
import { useRef, useState } from "react";
import { createPersonNote } from "../../api/outreach";
import { invalidate } from "../data/useQuery";
import { Button, Field, Kbd, modKey, SheetSection, Textarea, useToast } from "../kit";
import { hold, UNDO_MS } from "./hold";
import { addPending } from "./pending";

/** The server's limit on a note (`StaffNoteWriteSerializer`). */
export const NOTE_MAX = 2000;

export function AddNote({ personId }: { personId: number }) {
  const toast = useToast();
  const [body, setBody] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);
  const text = body.trim();

  const save = () => {
    if (!text) return;
    const note = body;
    const unshow = addPending(personId, "note", text);
    // The words come back only to an empty box: a note begun since is not overwritten.
    const restore = () => {
      unshow();
      setBody((now) => (now.trim() ? now : note));
    };
    const held = hold(() => {
      createPersonNote(personId, text).then(
        () => void invalidate(`v2:person:${personId}`).finally(unshow),
        () => {
          toast.show({ message: "Could not save the note" });
          restore();
        },
      );
    });
    setBody("");
    toast.show({
      message: "Note added",
      action: {
        label: "Undo",
        onAction: () => {
          held.cancel();
          restore();
        },
      },
      duration: UNDO_MS,
      onExpire: held.flush,
    });
    field.current?.focus();
  };

  return (
    <SheetSection title="Add a note">
      <Field label="New note" hideLabel>
        <Textarea
          ref={field}
          rows={2}
          placeholder="Who you spoke to, what was said"
          maxLength={NOTE_MAX}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              save();
            }
          }}
        />
      </Field>
      {text && (
        <Button variant="surface" size="sm" className="animate-fade self-end" aria-keyshortcuts="Meta+Enter Control+Enter" onClick={save}>
          Save
          <span aria-hidden>
            <Kbd>{modKey("↵")}</Kbd>
          </span>
        </Button>
      )}
    </SheetSection>
  );
}
